import { useMutation } from '@tanstack/react-query'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { patchJSON } from '@/lib/api'
import { apiFieldMessages } from '@/lib/contacts/errors'
import type { Person, ProfileMembership } from '@/lib/contacts/types'
import { displayDate } from '@/lib/dates'

interface MembershipResponse {
  membership: ProfileMembership
}

export function ReactivateMembershipDialog({
  person,
  membership,
  onClose,
  onReactivated,
}: {
  person: Person
  membership: ProfileMembership
  onClose: () => void
  onReactivated: () => Promise<void>
}) {
  const reactivateMembership = useMutation({
    mutationFn: () =>
      patchJSON<MembershipResponse>(`/api/v1/persons/${person.id}/membership/reactivate`, {
        membership: { payment_handling: 'offline' },
      }),
    onSuccess: onReactivated,
  })
  const fieldErrors = membershipFieldErrors(reactivateMembership.error)
  const error = fieldErrors.status || fieldErrors.paymentHandling || fieldErrors.membership

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <form
          onSubmit={(event) => {
            event.preventDefault()
            reactivateMembership.mutate()
          }}
        >
          <DialogHeader>
            <DialogTitle>Reactivate membership</DialogTitle>
            <DialogDescription>
              {person.name} · {membership.status} → Active
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col gap-2">
            {error && <Alert>{error}</Alert>}
            <PreviewRow label="Member number" value={memberNumberLabel(membership)} />
            <PreviewRow label="Renewal due" value={displayDate(membership.next_renewal_due_on)} />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={reactivateMembership.isPending}>
              {reactivateMembership.isPending ? 'Reactivating...' : 'Reactivate membership'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function membershipFieldErrors(error: Error | null) {
  return {
    membership: apiFieldMessages(error, 'membership')[0],
    paymentHandling: apiFieldMessages(error, 'payment_handling')[0],
    status: apiFieldMessages(error, 'status')[0],
  }
}

function memberNumberLabel(membership: ProfileMembership) {
  return membership.member_number ? `#${membership.member_number}` : '-'
}

function PreviewRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 text-body-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-foreground">{value}</span>
    </div>
  )
}
